import { isOnProduction } from '@/config/base'
import { Attributes, User } from '@/interfaces/users'
import { PROD_APP_ID, STAGING_OR_DEV_APP_ID } from '@/constants/intercom'
import { getAttributes } from '@/helpers/api'

const APP_ID = isOnProduction() ? PROD_APP_ID : STAGING_OR_DEV_APP_ID

export const loadIntercom = (): void => {
  const OSANO_ESSENTIAL_COOKIE_NAME = 'osano_consentmanager'
  const cookieExists = document.cookie.split(';').some((item) => item.trim().startsWith(OSANO_ESSENTIAL_COOKIE_NAME))

  window.intercomSettings = {
    app_id: APP_ID,
    hide_default_launcher: false,
    vertical_padding: cookieExists ? 35 : 162 // 132px height + 30px padding right to make it center https://www.intercom.com/help/en/articles/1118385-can-i-move-the-messenger-s-position-on-my-product-or-site
  }
  const INSIDE_CYPRESS_COOKIE = document.cookie.match('(^|;)\\s*inside_cypress\\s*=\\s*([^;]+)')?.pop() || ''
  const DISABLE_INTERCOM_COOKIE = document.cookie.match('(^|;)\\s*disable_intercom\\s*=\\s*([^;]+)')?.pop() || ''
  const loadIntercom = INSIDE_CYPRESS_COOKIE != 'true' || DISABLE_INTERCOM_COOKIE != 'true'
  if (loadIntercom) {
    ;(function (): void {
      const w = window as any
      const ic = w.Intercom
      if (typeof ic === 'function') {
        ic('reattach_activator')
        ic('update', w.intercomSettings)
      } else {
        const d = document
        const i = function (): void {
          i.c(arguments)
        }
        i.q = []
        i.c = function (args): void {
          i.q.push(args)
        }
        w.Intercom = i
        const l = function (): void {
          const s = d.createElement('script')
          s.type = 'text/javascript'
          s.async = true
          s.src = 'https://widget.intercom.io/widget/' + APP_ID
          const x = d.getElementsByTagName('script')[0]
          x.parentNode.insertBefore(s, x)
        }
        if (document.readyState === 'complete') {
          l()
        } else if (w.attachEvent) {
          w.attachEvent('onload', l)
        } else {
          w.addEventListener('load', l, false)
        }
      }
    })()
  }
}

export const updateIntercomUserDataSettings = async (userData: User): Promise<void> => {
  if (!window || !window.Intercom) {
    return
  }
  const attributes = await getAttributes(userData.uidExt)
  const productPlan = useGetUserProductPlan(attributes)

  intercomSettings.name = userData?.firstname + ' ' + userData?.lastname
  intercomSettings.email = userData?.emailAddress
  intercomSettings.user_hash = userData?.intercom.emailAddressHash
  intercomSettings.user = userData?.user
  intercomSettings.productPlan = productPlan
  intercomSettings.position = userData?.position
  window.intercomSettings = intercomSettings
  window.Intercom('update', intercomSettings)
}

export const updateIntercom = (): void => {
  window && window.Intercom && window.Intercom('update')
}

const useGetUserProductPlan = (attributes: Attributes): IntercomProductPlan => {
  if (!attributes) {
    return undefined
  }

  if (!attributes.isProfessional) {
    return IntercomProductPlan.COMMUNITY_FREE_12M
  }

  if (attributes.isTrial) {
    if (attributes.isAcademic) {
      return IntercomProductPlan.ACADEMIC_TEST
    }
    return IntercomProductPlan.PROFESSIONAL_TEST
  }

  if (attributes.isAcademic) {
    return IntercomProductPlan.ACADEMIC_PRIVATE
  }

  return IntercomProductPlan.PROFESSIONAL_12M_FIXED_QUOTA
}

enum IntercomProductPlan {
  'PROFESSIONAL_12M_FIXED_QUOTA' = 'PROFESSIONAL_12M_FIXED_QUOTA',
  'PROFESSIONAL_TEST' = 'PROFESSIONAL_TEST',
  'ACADEMIC_PRIVATE' = 'ACADEMIC_PRIVATE',
  'ACADEMIC_TEST' = 'ACADEMIC_TEST',
  'COMMUNITY_FREE_12M' = 'COMMUNITY_FREE_12M'
}
